import bridge from '@/plugins/vue-constructor-bridge';

import returnAppConfig from '@/helpers/return-app-config';

export const setIframeStyle = () => {
  const iframe = document.getElementById('3dsIframeId');

  iframe.style.width = '500px';
  iframe.style.height = '300px';
  iframe.style.zIndex = '101';
  iframe.style.position = 'fixed';
  iframe.style.top = '50%';
  iframe.style.left = '50%';
  iframe.style.transform = 'translate(-50%, -50%)';
};

export const getTdsConfig = (tdsProviderDetails, setIframeStyleFn) => {
  const showChallenge = tdsProviderDetails.threeDsFriction;

  /** https://docs.3dsintegrator.com/docs/js-sdk-options for list of sdk options */
  const options = {
    autoSubmit: false,
    endpoint: tdsProviderDetails?.threeDsApiUrl,
    addResultToForm: false,
    verbose: !returnAppConfig().isProductionEnvironment,
    challengeIndicator: '04',
    showChallenge,
    allowRetry: false, // allowRetry can only be true when autoSubmit is true
    timeout: showChallenge ? tdsProviderDetails.threeDsChallengeTimeout : '3',
    forcedTimeout: showChallenge ? tdsProviderDetails.threeDsChallengeForcedTimeout : '3',
    iframeId: '3dsIframeId',
    prompt: setIframeStyleFn
  };

  const threeDsOptionsConfig = [
    'billing-form',
    tdsProviderDetails.threeDsApiPublicKey,
    options
  ];

  return threeDsOptionsConfig;
};

export const initPaayTds = (threeDsOptionsConfig) => {
  const [billingFormId, apiKey, options] = threeDsOptionsConfig;

  if (window.ThreeDS) {
    bridge.triggerNewRelicEvent('PaaySdkInitialization', 'Info', 'Sdk loaded on page');

    /** https://docs.3dsintegrator.com/docs/3ds-with-javascript-sdk */
    const paayTds = new window.ThreeDS(
      billingFormId,
      apiKey,
      null,
      options
    );

    return paayTds;
  }

  bridge.triggerNewRelicEvent('PaaySdkInitialization', 'Error', 'Sdk not loaded on page');

  return null;
};

export const parseResponseBody = (response) => {
  const {
    xid = null,
    dsTransId = null,
    protocolVersion,
    eci,
    cavv = null,
    authenticationValue = null,
    status
  } = response;

  return {
    status,
    threeDsTransactionId: xid ?? dsTransId,
    cavv: cavv ?? authenticationValue,
    eciIndicator: eci,
    programProtocol: protocolVersion
  };
};

export const successWrapper = (responseCb, errorCb, continueTransactionOnFail = false) => (response) => {
  /** https://docs.3dsintegrator.com/docs/3ds-response-table-of-content for list of status values */
  if (response.status === 'Y' || response.status === 'A') {
    const parsedResponse = parseResponseBody(response);

    bridge.triggerNewRelicEvent('PaayVerificationComplete', 'Information', 'Valid Response', { paayResponse: { ...parsedResponse, cavv: 'redacted' } });

    responseCb(parsedResponse);
  }
  else if (continueTransactionOnFail) {
    const data = {
      paayResponse: {
        continueTransactionOnFail,
        status: response.status,
        transStatusReason: response.transStatusReason,
        transStatusReasonDetail: response.transStatusReasonDetail,
        dsTransId: response.dsTransId
      }
    };

    bridge.triggerNewRelicEvent('PaayVerificationComplete', 'Warning', 'Invalid Response - Continue or Fail', data);

    responseCb();
  }
  else {
    const data = {
      paayResponse: {
        status: response.status,
        transStatusReason: response.transStatusReason,
        transStatusReasonDetail: response.transStatusReasonDetail,
        dsTransId: response.dsTransId
      }
    };

    bridge.triggerNewRelicEvent('PaayVerificationComplete', 'Error', 'Invalid Response', data);

    errorCb();
  }
};

/**
 * Paay returns stringified json errors vs. an actual error object.
 * When the challenge modal is triggered by Paay the challenge modal "polls/listens" for user interactions.
 * If a user hasn't interacted with the modal over a few seconds an error is logged (ie.'No result found for transaction as yet. Please subscribe again').
 * The challenge modal should close for any other error that is not the "polling" error (ie. inactivityErrorMessage) because we want the modal to stay open for users.
 * A default stringified error is needed in order for JSON.parse() to parse other other "non-polling" errors.
*/
export const failureWrapper = (responseCb, errorCb, continueTransactionOnFail = false) => (error = '{ "error": null }') => {
  const inactivityErrorMessage = 'No result found for transaction as yet. Please subscribe again';
  const parsedError = JSON.parse(error);

  if (continueTransactionOnFail) {
    bridge.triggerNewRelicEvent('PaayVerificationError', 'Warning', 'Paay Verification Error - Continue or Fail', { continueTransactionOnFail, error: parsedError.error, errorDetail: parsedError.errorDetail });

    responseCb();
  }
  else if (parsedError.error === inactivityErrorMessage) {
    bridge.triggerNewRelicEvent('PaayVerificationError', 'Warning', 'Inactivity Error', { error: parsedError.error, errorDetail: parsedError.errorDetail });
  }
  else {
    bridge.triggerNewRelicEvent('PaayVerificationError', 'Error', 'Unexpected Error', { error: parsedError.error, errorDetail: parsedError.errorDetail });

    errorCb();
  }
};

export const submitTdsTransaction = (paayTds, continueTransactionOnFail, orderDetails) => (responseCb, errorCb) => {
  /** https://docs.3dsintegrator.com/docs/verify-method */
  if (paayTds?.verify) {
    bridge.triggerNewRelicEvent('PaayInitializationSuccess', 'Info', 'Calling paay.verify()');

    paayTds.verify(
      successWrapper(responseCb, errorCb, continueTransactionOnFail),
      failureWrapper(responseCb, errorCb, continueTransactionOnFail),
      { amount: orderDetails.total }
    );
  }
  else if (continueTransactionOnFail) {
    bridge.triggerNewRelicEvent('PaayInitializationError', 'Warning', 'Paay Init Error - Continue or Fail', { continueTransactionOnFail });

    responseCb();
  }
  else {
    bridge.triggerNewRelicEvent('PaayInitializationError', 'Error', 'Paay Init Error');

    errorCb();
  }
};
